import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '../../services/api';
import AdminLayout from '../../layouts/AdminLayout';
import StatCard from '../../components/common/StatCard';

const COLORS = ['#f08413', '#138808', '#1a56db', '#e02424'];

export default function AdminDashboard() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/dashboard').then((r) => {
      setData(r.data.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <AdminLayout>
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-brand-500 border-t-transparent rounded-full"></div>
      </div>
    </AdminLayout>
  );

  const n = (v) => Number(v) || 0;

  const stats = [
    { title: 'Total Workers',         value: n(data?.workers?.total),                sub: `${n(data?.workers?.verified)} verified`,              color: 'brand'  },
    { title: 'Employers',             value: n(data?.employers?.total),               sub: `${n(data?.employers?.verified)} verified`,             color: 'green'  },
    { title: 'Active Requirements',   value: n(data?.requirements?.active),           sub: `${n(data?.requirements?.pending_approval)} pending`,   color: 'blue'   },
    { title: 'Total Revenue (₹)',     value: `₹${n(data?.payments?.total_amount)}`,   sub: `${n(data?.payments?.total)} transactions`,             color: 'purple' },
    { title: 'Pending Verifications', value: n(data?.pending_verifications?.total),   sub: 'Workers awaiting review',                              color: 'red'    },
    { title: 'Matches (30d)',         value: n(data?.active_matches?.total),          sub: 'In last 30 days',                                      color: 'orange' },
  ];

  const workerPie = [
    { name: 'Verified', value: n(data?.workers?.verified) },
    { name: 'Pending',  value: n(data?.workers?.total) - n(data?.workers?.verified) - n(data?.workers?.expired) },
    { name: 'Expired',  value: n(data?.workers?.expired) },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <span className="text-xs sm:text-sm text-gray-500 text-right hidden xs:block">{new Date().toLocaleDateString('en-IN', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}</span>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {stats.map((s) => (
            <StatCard key={s.title} {...s} />
          ))}
        </div>

        {/* Action Items */}
        {data?.pending_verifications?.total > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-yellow-800">{data.pending_verifications.total} Workers Pending Verification</p>
              <p className="text-sm text-yellow-600">Review and approve/reject worker profiles</p>
            </div>
            <Link to="/admin/workers?verification_status=pending"
              className="bg-yellow-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-yellow-700 self-start sm:self-auto whitespace-nowrap">
              Review Now
            </Link>
          </div>
        )}

        {data?.requirements?.pending_approval > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-blue-800">{data.requirements.pending_approval} Requirements Pending Approval</p>
              <p className="text-sm text-blue-600">Review and publish employer requirements</p>
            </div>
            <Link to="/admin/requirements?status=pending_approval"
              className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700 self-start sm:self-auto whitespace-nowrap">
              Review Now
            </Link>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Worker Status Pie */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Worker Status Distribution</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={workerPie} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value">
                  {workerPie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(value, name) => [value, name]} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-2">
              {workerPie.map((entry, i) => (
                <div key={entry.name} className="flex items-center gap-1.5 text-sm text-gray-600">
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                  {entry.name}: <span className="font-medium text-gray-900">{entry.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Recent Activity</h3>
            <div className="space-y-3 max-h-52 overflow-y-auto">
              {(data?.recentActivity || []).map((a, i) => (
                <div key={i} className="flex items-start gap-3 text-sm">
                  <span className="text-gray-400 text-xs mt-0.5 flex-shrink-0 font-mono">
                    {new Date(a.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <div>
                    <span className="font-medium text-gray-700">{a.action}</span>
                    {a.mobile && <span className="text-gray-400 ml-1">by {a.mobile}</span>}
                  </div>
                </div>
              ))}
              {!data?.recentActivity?.length && <p className="text-sm text-gray-400">No recent activity</p>}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
